import {
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  HourglassEmpty as HourglassIcon,
  Error as ErrorIcon,
  MarkEmailRead as DeliveredIcon,
  Send as SentIcon,
  Reply as RespondedIcon,
  Visibility as ReadIcon,
  Phone as AnsweredIcon,
  CallEnd as NotAnsweredIcon,
  Voicemail as VoicemailIcon,
  Block as BusyIcon,
  RingVolume as RingingIcon,
  AccessTime,
  CheckCircle,
  Error,
  HourglassEmpty,
} from '@mui/icons-material'

import {
  ECampaignContactGoalState,
  ECampaignContactSentiment,
  ECampaignContactStatus,
  CONTACT_STATUS_LABELS,
  EOutreachStatus,
  EUploadLogStatus,
  EUploadLogSourceType,
} from '~/features/campaigns/models'
import { EChannelKeys, ESpeaker } from '~/features/inbox/models'
import { formatLocaleDate } from '~/utils/date'
import rem from '~/utils/ui'

// eslint-disable-next-line import/prefer-default-export
export const CREATE_CAMPAIGN_DEFAULT_ID = 'creating-new-campaign'
const defaultCampaignDateLabel = formatLocaleDate(new Date(), {
  withYear: true,
  month: 'long',
})

export const DEFAULT_CAMPAIGN_NAME = `Campaign ${defaultCampaignDateLabel}`
export const DEFAULT_OFU_CAMPAIGN_NAME = `Follow-up Campaign ${defaultCampaignDateLabel}`
export const INSERT_INPUT_MAX_LENGTH = 500
export const CAMPAIGN_DEFAULT_MERGE_FIELDS = {
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  PHONE_NUMBER: 'Phone Number',
  EMAIL: 'Email',
}

export const DEFAULT_CAMPAIGN_QUESTION =
  "You don't need to ask any questions. Just be helpful and always ask if they have any questions they need help with."
export const CAMPAIGN_EXECUTION_INTERVAL_SECONDS = 10
export const CAMPAIGN_STATUS_REFETCH_INTERVAL = 5

export const API_WEBHOOK_EXAMPLE_HEADERS = `{
  'Content-Type': 'application/json',
  'x-api-key': '<api-key>'
}`

export const API_WEBHOOK_EXAMPLE_BODY = `{
 "contacts": [
    {
      "firstName": "Ava",
      "lastName": "Lopez",
      "email": "ava@example.com",
      "phone": "15551234567",
      "contactMetadata": {
        "source": "web_form",
        "tier": "trial"
      }
    }
  ]
}`
// TODO: update this to the actual help url when we have it
export const CAMPAIGN_CONTACTS_WEBHOOK_HELP_URL =
  'https://docs.replify.ai/api-reference/campaigns/manage-campaign-contacts'

export const PER_CONTACT_ROUTING_EXAMPLE = `{
  "contacts": [
    {
      "firstName": "Ava",
      "lastName": "Lopez",
      "email": "ava@example.com",
      "externalIdKey": "hubspotId",
      "externalIdValue": "1234"
    },
    {
      "firstName": "Ben",
      "lastName": "Nguyen",
      "email": "ben@example.com",
      "externalIdKey": "hubspotId",
      "externalIdValue": "5678"
    }
  ]
}`

export const BATCH_ROUTING_EXAMPLE = `{
  "externalIdKey": "hubspotId",
  "externalIdValue": "1234",
  "contacts": [
    {
      "firstName": "Ana",
      "lastName": "Lopez",
      "email": "ava@example.com"
    }
  ]
}`

export const ICON_SIZE_BY_CHANNEL: Record<EChannelKeys, number> = {
  [EChannelKeys.phone]: 40,
  [EChannelKeys.email]: 60,
  [EChannelKeys.text]: 30,
  [EChannelKeys.chat]: 40,
}

export const OUTREACH_STATUS_DISPLAY_NAMES: Record<EOutreachStatus, string> = {
  [EOutreachStatus.pending]: 'Pending',
  [EOutreachStatus.inProgress]: 'In Progress',
  [EOutreachStatus.failed]: 'Failed',
  [EOutreachStatus.sent]: 'Sent',
  [EOutreachStatus.delivered]: 'Delivered',
  [EOutreachStatus.responded]: 'Responded',
  [EOutreachStatus.read]: 'Read',
  [EOutreachStatus.answered]: 'Answered',
  [EOutreachStatus.notAnswered]: 'Not Answered',
  [EOutreachStatus.voicemail]: 'Voicemail',
  [EOutreachStatus.busy]: 'Busy',
  [EOutreachStatus.noAnswer]: 'Not Answered',
  [EOutreachStatus.noAnswer2]: 'Not Answered',
  [EOutreachStatus.cancelled]: 'Cancelled',
  [EOutreachStatus.ringing]: 'Ringing',
}

// Status to icon mapping
export const STATUS_ICON_MAP: Record<EOutreachStatus, React.ElementType> = {
  [EOutreachStatus.pending]: ScheduleIcon,
  [EOutreachStatus.inProgress]: HourglassIcon,
  [EOutreachStatus.failed]: ErrorIcon,
  [EOutreachStatus.sent]: SentIcon,
  [EOutreachStatus.delivered]: DeliveredIcon,
  [EOutreachStatus.responded]: RespondedIcon,
  [EOutreachStatus.read]: ReadIcon,
  [EOutreachStatus.answered]: AnsweredIcon,
  [EOutreachStatus.notAnswered]: NotAnsweredIcon,
  [EOutreachStatus.voicemail]: VoicemailIcon,
  [EOutreachStatus.busy]: BusyIcon,
  [EOutreachStatus.noAnswer]: NotAnsweredIcon,
  [EOutreachStatus.noAnswer2]: NotAnsweredIcon,
  [EOutreachStatus.cancelled]: CancelIcon,
  [EOutreachStatus.ringing]: RingingIcon,
}

export const EDIT_OUTREACH_FORM_ID = 'edit-outreach-form'
export const OUTREACH_TIME_ASAP_VALUE = 'as-soon-as-possible'

export const MESSAGE_TYPES = [
  { value: ESpeaker.user, label: 'User' },
  { value: ESpeaker.assistant, label: 'Assistant' },
] as const

export enum ECustomPropertyValue {
  city = 'city',
  state = 'state',
  zipCode = 'zipCode',
  country = 'country',
  streetAddress1 = 'streetAddress1',
  streetAddress2 = 'streetAddress2',
  custom = 'custom',
}

export const CUSTOM_PROPERTY_TYPES = [
  { value: ECustomPropertyValue.city, label: 'City' },
  { value: ECustomPropertyValue.state, label: 'State' },
  { value: ECustomPropertyValue.zipCode, label: 'Zip Code' },
  { value: ECustomPropertyValue.country, label: 'Country' },
  { value: ECustomPropertyValue.streetAddress1, label: 'Street Address 1' },
  { value: ECustomPropertyValue.streetAddress2, label: 'Street Address 2' },
  { value: ECustomPropertyValue.custom, label: 'Custom' },
] as const

export enum ETableColumns {
  check = 'check',
  name = 'name',
  email = 'email',
  phone = 'phone',
  status = 'status',
  execution = 'execution',
  actions = 'actions',
}

type TColumn = {
  id: ETableColumns
  label: string
  maxWidth: number
  px?: number
  minWidth?: number
}

export const TABLE_COLUMNS: TColumn[] = [
  { id: ETableColumns.check, label: '', maxWidth: 58, px: 1, minWidth: 58 },
  { id: ETableColumns.name, label: 'Name', maxWidth: 190, minWidth: 60 },
  { id: ETableColumns.email, label: 'Email', maxWidth: 190, minWidth: 60 },
  { id: ETableColumns.phone, label: 'Phone', maxWidth: 170, minWidth: 60 },
  { id: ETableColumns.status, label: 'Status', maxWidth: 180, minWidth: 180 },
  {
    id: ETableColumns.execution,
    label: 'Execution',
    maxWidth: 200,
    minWidth: 200,
  },
  { id: ETableColumns.actions, label: 'Actions', maxWidth: 100, minWidth: 100 },
] as const

export enum EContactFilterValues {
  all = 'all',
  hasExecutedOutreach = 'hasExecutedOutreach',
  hasContactInteraction = 'hasContactInteraction',
  hasLastExecutedOutreachInteraction = 'hasLastExecutedOutreachInteraction',
  hasContactInteractionAndNotDisqualified = 'hasContactInteractionAndNotDisqualified',
  planning = 'planning',
  executing = 'executing',
  completed = 'completed',
  failed = 'failed',
  notStarted = 'notStarted',
  disabled = 'disabled',
  disqualified = 'disqualified',
  needsEvaluation = 'needsEvaluation',
}

export const OFU_CONTACTS_FILTER_OPTIONS = [
  { label: 'All', value: EContactFilterValues.all },
  {
    label: 'Got 1+ execution',
    value: EContactFilterValues.hasExecutedOutreach,
  },
  {
    label: 'Got 1+ Responses',
    value: EContactFilterValues.hasContactInteraction,
  },
  {
    label: 'Got 1+ Responses(Not Disqualified)',
    value: EContactFilterValues.hasContactInteractionAndNotDisqualified,
  },
  {
    label: 'Responded to last outreach',
    value: EContactFilterValues.hasLastExecutedOutreachInteraction,
  },
]

const CONTACT_STATUS_FILTER_OPTIONS = (
  [
    ECampaignContactStatus.planning,
    ECampaignContactStatus.started,
    ECampaignContactStatus.executing,
    ECampaignContactStatus.completed,
    ECampaignContactStatus.disqualified,
    ECampaignContactStatus.disabled,
    ECampaignContactStatus.needsApproval,
  ] as const
)
  .map((value) => ({
    label: CONTACT_STATUS_LABELS[value],
    value,
  }))
  .sort((a, b) => a.label.localeCompare(b.label))

export enum EContactFilterGroups {
  status = 'Status',
  execution = 'Execution',
  response = 'Response',
  sourceType = 'Source Type',
  assistant = 'Assistant',
  goal = 'Goal',
  sentiment = 'Sentiment',
}

export const CONTACT_GOAL_STATE_LABELS: Record<
  ECampaignContactGoalState,
  string
> = {
  [ECampaignContactGoalState.achieved]: 'Achieved',
  [ECampaignContactGoalState.notAchieved]: 'Not achieved',
  [ECampaignContactGoalState.notAnalyzed]: 'Not analyzed yet',
}

export const CONTACT_SENTIMENT_LABELS: Record<
  ECampaignContactSentiment,
  string
> = {
  [ECampaignContactSentiment.positive]: 'Positive',
  [ECampaignContactSentiment.neutral]: 'Neutral',
  [ECampaignContactSentiment.negative]: 'Negative',
  [ECampaignContactSentiment.unknown]: 'Unknown',
}

export const OFU_CONTACTS_FILTER_GROUPS = [
  {
    groupLabel: EContactFilterGroups.status,
    options: CONTACT_STATUS_FILTER_OPTIONS,
  },
  {
    groupLabel: EContactFilterGroups.execution,
    options: [
      {
        label: '1+ Executed',
        value: EContactFilterValues.hasExecutedOutreach,
      },
    ],
  },
  {
    groupLabel: EContactFilterGroups.response,
    options: [
      {
        label: '1+ Reply',
        value: EContactFilterValues.hasContactInteraction,
      },
      {
        label: '1+ Reply(Not Disqualified)',
        value: EContactFilterValues.hasContactInteractionAndNotDisqualified,
      },
      {
        label: 'Replied to last outreach',
        value: EContactFilterValues.hasLastExecutedOutreachInteraction,
      },
    ],
  },
  {
    groupLabel: EContactFilterGroups.sourceType,
    options: [
      {
        label: 'Manual',
        value: EUploadLogSourceType.uiManualUpload,
      },
      {
        label: 'CSV Upload',
        value: EUploadLogSourceType.uiCsvUpload,
      },
      {
        label: 'API',
        value: EUploadLogSourceType.apiUpload,
      },
      {
        label: 'Email CSV',
        value: EUploadLogSourceType.emailCsvUpload,
      },
      {
        label: 'Populate campaign',
        value: EUploadLogSourceType.testCampaignPopulate,
      },
    ],
  },
  {
    groupLabel: EContactFilterGroups.assistant,
    options: [],
  },
  {
    groupLabel: EContactFilterGroups.goal,
    options: Object.values(ECampaignContactGoalState).map((value) => ({
      label: CONTACT_GOAL_STATE_LABELS[value],
      value,
    })),
  },
  {
    groupLabel: EContactFilterGroups.sentiment,
    options: [
      ECampaignContactSentiment.positive,
      ECampaignContactSentiment.neutral,
      ECampaignContactSentiment.negative,
      ECampaignContactSentiment.unknown,
    ].map((value) => ({ label: CONTACT_SENTIMENT_LABELS[value], value })),
  },
]

export const OUTREACH_EXECUTED_STATUSES = [
  EOutreachStatus.sent,
  EOutreachStatus.delivered,
  EOutreachStatus.responded,
  EOutreachStatus.read,
  EOutreachStatus.answered,
  EOutreachStatus.voicemail,
  EOutreachStatus.busy,
  EOutreachStatus.noAnswer,
  EOutreachStatus.noAnswer2,
  EOutreachStatus.notAnswered,
  EOutreachStatus.cancelled,
  EOutreachStatus.ringing,
  EOutreachStatus.inProgress,
]

/**
 * Statuses where no outreach actually went out, so they must not be counted as an attempt.
 * Mirrors NON_EXECUTED_OUTREACH_STATUSES in campaign-service and growth-service, so "did an
 * outreach go out" means the same thing everywhere.
 *
 * `cancelled` is the subtle one: growth creates the outreach, then cancels it unsent when the
 * execution-time gate fires — most often the max-outreach cap itself. Counting it produced the
 * contradiction of "4 / 3" attempts against a cap of 3, where the 4th is the row the cap
 * cancelled. Do not confuse this with OUTREACH_EXECUTED_STATUSES above, which deliberately
 * includes `cancelled` because it answers "was this outreach ever touched" for reporting.
 */
export const OUTREACH_NON_EXECUTED_STATUSES = [
  EOutreachStatus.pending,
  EOutreachStatus.failed,
  EOutreachStatus.cancelled,
]

export const CONTACT_INTERACTED_STATUSES = [
  EOutreachStatus.responded,
  EOutreachStatus.answered,
]

export const STATUS_MAP = {
  [EUploadLogStatus.notStarted]: {
    label: 'Not Started',
    backgroundColor: 'grey.200',
    color: 'grey.dark',
    icon: <HourglassEmpty sx={{ fontSize: rem(16) }} />,
  },
  [EUploadLogStatus.processing]: {
    label: 'Processing',
    backgroundColor: 'info.dark',
    color: 'common.white',
    icon: <AccessTime sx={{ fontSize: rem(16) }} />,
  },
  [EUploadLogStatus.completed]: {
    label: 'Completed',
    backgroundColor: 'success.light',
    color: 'common.white',
    icon: <CheckCircle sx={{ fontSize: rem(16) }} />,
  },
  [EUploadLogStatus.failed]: {
    label: 'Failed',
    backgroundColor: 'error.dark',
    color: 'common.white',
    icon: <Error sx={{ fontSize: rem(16) }} />,
  },
  [EUploadLogStatus.partialFailed]: {
    label: 'Partial Failed',
    backgroundColor: 'warning.dark',
    color: 'common.white',
    icon: <CheckCircle sx={{ fontSize: rem(16) }} />,
  },
  default: {
    label: 'Unknown',
    backgroundColor: 'grey.200',
    color: 'grey.dark',
    icon: <HourglassEmpty sx={{ fontSize: rem(16) }} />,
  },
}

export const CADENCE_DESCRIPTIONS: Record<string, string> = {
  aggressive: 'Aggressive – Send follow-ups daily.',
  moderate: 'Moderate – Send follow-ups every 2 days.',
  balanced: 'Balanced – Send follow-ups every 3–4 days.',
  relaxed: 'Relaxed – Send follow-ups weekly.',
  custom:
    'Custom – Define your own schedule below. Describe in plain English and we will interpret it.',
}

export const OUTREACH_TIME_START = 8
export const OUTREACH_TIME_END = 20
export const OUTREACH_TIME_OPTIONS = Array.from(
  { length: OUTREACH_TIME_END - OUTREACH_TIME_START + 1 },
  (_, i) => {
    const hour = i + OUTREACH_TIME_START
    const timeString = `${hour.toString().padStart(2, '0')}:00`

    let displayTime: string
    if (hour === 12) {
      displayTime = '12:00 PM'
    } else if (hour < 12) {
      displayTime = `${hour}:00 AM`
    } else {
      displayTime = `${hour - 12}:00 PM`
    }

    return { value: timeString, label: displayTime }
  },
)

/** Legacy option for campaigns that had 9 PM selected before range was limited to 8 AM–8 PM. */
export const OUTREACH_TIME_LEGACY_9PM_OPTION = {
  value: '21:00',
  label: '9:00 PM',
}

export const DEFAULT_OFU_QUALIFICATION_CRITERIA =
  "Anyone who hasn't said no or wants to unsubscribe"

export const MAX_LENGTH_TEXT_MESSAGE = 350
export const MAX_LENGTH_PHONE_VOICE_MESSAGE = 1000
export const MAX_LENGTH_PHONE_INTRO_MESSAGE = 1000
export const MAX_LENGTH_EMAIL_SUBJECT_MESSAGE = 200
export const MAX_LENGTH_EMAIL_CONTENT_MESSAGE = 5000

/** Schedule step content limits — aligned with campaignService validators. */
export const SCHEDULE_STEP_MAX_LENGTH_TEXT_MESSAGE = 1000
export const SCHEDULE_STEP_MAX_LENGTH_EMAIL_SUBJECT = 255
export const SCHEDULE_STEP_MAX_LENGTH_CALL_SCRIPT = 1000
export const SCHEDULE_STEP_MAX_LENGTH_VOICEMAIL_SCRIPT = 1000

export const CONTACT_DETAIL_REFETCH_INTERVAL_MS = 5000

export const DEFAULT_MAX_REACHOUTS = 3
export const MIN_REACHOUTS = 1

export const MAX_REACHOUTS_LIMIT = 20
